

import { useState } from "react";
import SearchableDropdown from "../components/SearchableDropdown";

interface RoomFormData {
  name: string;
  type: string;
  capacity: string;
  rate: string;
  status: string;
  notes: string;
}

interface RoomFormProps {
  onSubmit: (data: RoomFormData) => void;
  onCancel: () => void;
  initialData?: Partial<RoomFormData>;
}

const statuses = ["available", "booked"];

export default function RoomForm({ onSubmit, onCancel, initialData }: RoomFormProps) {
  const [formData, setFormData] = useState<RoomFormData>({
    name: initialData?.name || "",
    type: initialData?.type || "",
    capacity: initialData?.capacity || "",
    rate: initialData?.rate || "",
    status: initialData?.status || "available",
    notes: initialData?.notes || "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof RoomFormData, string>>>({});
  const [roomTypes, setRoomTypes] = useState(["Banquet", "Open Air", "Conference", "Accommodation"]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof RoomFormData, string>> = {};
    
    if (!formData.name.trim()) newErrors.name = "Room name is required";
    if (!formData.type) newErrors.type = "Room type is required";
    if (!formData.capacity || parseInt(formData.capacity) < 1) newErrors.capacity = "Valid capacity is required";
    if (!formData.rate || parseFloat(formData.rate) < 0) newErrors.rate = "Valid rate is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof RoomFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: keyof RoomFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Room Name */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Room Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="e.g., Grand Ballroom"
            className={inputClass("name")}
          />
          {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
        </div>

        {/* Room Type */}
        <div>
          <SearchableDropdown
            label="Room Type *"
            options={roomTypes.map(t => ({ value: t, label: t }))}
            value={formData.type}
            onChange={(value) => handleChange("type", value)}
            placeholder="Select or type room type"
            allowAddNew
            onAddNew={(value) => {
              setRoomTypes(prev => [...prev, value]);
              handleChange("type", value);
            }}
            error={errors.type}
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className={inputClass("status")}
          >
            {statuses.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Capacity */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Capacity (persons) *</label>
          <input
            type="number"
            min="1"
            value={formData.capacity}
            onChange={(e) => handleChange("capacity", e.target.value)}
            placeholder="e.g., 500"
            className={inputClass("capacity")}
          />
          {errors.capacity && <p className="text-xs text-rose-500 mt-1">{errors.capacity}</p>}
        </div>

        {/* Rate */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Rate per Day (₹) *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.rate}
            onChange={(e) => handleChange("rate", e.target.value)}
            placeholder="0.00"
            className={inputClass("rate")}
          />
          {errors.rate && <p className="text-xs text-rose-500 mt-1">{errors.rate}</p>}
        </div>

        {/* Notes */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Features, amenities, restrictions..."
            rows={3}
            className={`${inputClass("notes")} resize-none`}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update Room" : "Add Room"}
        </button>
      </div>
    </form>
  );
}
